import { useEffect, useState, useCallback } from "react";
import api, { apiError, downloadCsv } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, dt } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Plus, Search, Link2, Copy, Loader2, CheckCircle2, XCircle, Download } from "lucide-react";
import { toast } from "sonner";

const FRONTEND = window.location.origin;

export default function Transactions() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [amount, setAmount] = useState("");
  const [openAmount, setOpenAmount] = useState(false);
  const [customer, setCustomer] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    api.get("/transactions", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const createOrder = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/orders", {
        amount: openAmount ? 0 : parseFloat(amount),
        open_amount: openAmount,
        customer_name: customer || "Customer",
        note: "Collection",
      });
      setCreated(data);
      setAmount(""); setCustomer(""); setOpenAmount(false);
      toast.success(`Order ${data.order_id} created`);
      load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const act = async (id, kind) => {
    try {
      await api.post(`/transactions/${id}/${kind}`, {});
      toast.success(kind === "confirm" ? "Marked as success" : "Marked as failed");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const handleExport = () => {
    const params = status !== "all" ? { status } : {};
    downloadCsv("/transactions/export", params, `woltpe_transactions_${Date.now()}.csv`)
      .catch(() => toast.error("Export failed"));
  };

  const payLink = created ? `${FRONTEND}/pay/${created.order_id}` : "";
  const isMerchant = user.role === "merchant";
  const title = isMerchant ? "Collections" : "Transactions";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{title}</h2>
          <p className="text-sm text-muted-foreground">Every UPI collection, reconciled and reconcilable.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-csv-button">
            <Download className="mr-1.5 h-4 w-4" /> Export CSV
          </Button>
        {isMerchant && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreated(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-collection-button"><Plus className="mr-1.5 h-4 w-4" /> New collection</Button>
            </DialogTrigger>
            <DialogContent data-testid="new-collection-dialog">
              <DialogHeader>
                <DialogTitle>Create a collection</DialogTitle>
                <DialogDescription>A UPI ID is auto-selected by rotation and a unique-paise amount is assigned.</DialogDescription>
              </DialogHeader>
              {!created ? (
                <form onSubmit={createOrder} className="space-y-4">
                  <label className="flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer" htmlFor="open-amt">
                    <Checkbox id="open-amt" checked={openAmount} onCheckedChange={(v) => setOpenAmount(!!v)} data-testid="order-open-amount-checkbox" className="mt-0.5" />
                    <span className="text-sm">
                      <span className="font-medium">Let the customer enter the amount</span>
                      <span className="block text-xs text-muted-foreground">No fixed amount — the payer types how much to pay on the payment page.</span>
                    </span>
                  </label>
                  <div className="space-y-1.5">
                    <Label htmlFor="amt">Amount (₹)</Label>
                    <Input id="amt" type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                      placeholder={openAmount ? "Customer will enter the amount" : "499"} required={!openAmount} disabled={openAmount} data-testid="order-amount-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cust">Customer name</Label>
                    <Input id="cust" value={customer} onChange={(e) => setCustomer(e.target.value)}
                      placeholder="Optional" data-testid="order-customer-input" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving} data-testid="order-submit-button">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate payment link"}
                    </Button>
                  </DialogFooter>
                </form>
              ) : (
                <div className="space-y-4" data-testid="order-created-panel">
                  <div className="rounded-xl border bg-muted/40 p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="font-mono">{created.order_id}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono font-semibold">{created.open_amount ? "Customer enters amount" : inr(created.amount)}</span></div>
                    {!created.open_amount && <div className="flex justify-between"><span className="text-muted-foreground">UPI ID</span><span className="font-mono">{created.vpa}</span></div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={payLink} className="font-mono text-xs" data-testid="order-pay-link" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Link copied"); }}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <a href={payLink} target="_blank" rel="noreferrer">
                    <Button className="w-full" variant="secondary"><Link2 className="mr-1.5 h-4 w-4" /> Open payment page</Button>
                  </a>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Tabs value={status} onValueChange={setStatus}>
            <TabsList data-testid="status-filter-tabs">
              {["all", "success", "pending", "utr_submitted", "failed"].map((s) => (
                <TabsTrigger key={s} value={s} data-testid={`filter-${s}`} className="capitalize">
                  {s === "utr_submitted" ? "UTR" : s}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Order ID, UTR, customer, VPA…"
              className="pl-9" data-testid="txn-search-input" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>UPI ID</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {!isMerchant && <TableHead className="text-right">Fee</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>UTR</TableHead>
                <TableHead>Time</TableHead>
                {isMerchant && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {rows && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No transactions found.</TableCell></TableRow>
              )}
              {rows && rows.map((t) => (
                <TableRow key={t.id} data-testid={`txn-row-${t.order_id}`}>
                  <TableCell className="font-mono text-xs">{t.order_id}</TableCell>
                  <TableCell className="whitespace-nowrap">{t.customer_name}</TableCell>
                  <TableCell className="font-mono text-xs max-w-[160px] truncate">{t.vpa}</TableCell>
                  <TableCell className="text-right font-mono font-medium">{inr(t.amount)}</TableCell>
                  {!isMerchant && <TableCell className="text-right font-mono text-xs text-warning">{inr(t.platform_fee)}</TableCell>}
                  <TableCell><StatusBadge status={t.status} /></TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.utr || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{dt(t.created_at)}</TableCell>
                  {isMerchant && (
                    <TableCell className="text-right">
                      {["pending", "utr_submitted"].includes(t.status) ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="icon" variant="outline" className="h-8 w-8 text-success" onClick={() => act(t.id, "confirm")} data-testid={`confirm-${t.order_id}`}>
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => act(t.id, "fail")} data-testid={`fail-${t.order_id}`}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
